/**
 * AdminEvalDashboard Component
 * Q AI Eval Pipeline Management Dashboard
 *
 * Features:
 * - Dashboard with score trends and category breakdown
 * - View/manage eval cases
 * - Start new eval runs
 * - View run results with scores
 * - Compare versions side-by-side
 * - Export results for manual review
 */

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    Pagination,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Alert,
    Tabs,
    Tab
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    MoreVert as MoreVertIcon,
    PlayArrow as PlayArrowIcon,
    Download as DownloadIcon,
    Compare as CompareIcon,
    Assessment as AssessmentIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Category as CategoryIcon,
    Quiz as QuizIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Timeline as TimelineIcon,
    Score as ScoreIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import {
    getEvalDashboard,
    getEvalCases,
    getEvalRuns,
    getEvalRunById,
    startEvalRun,
    compareVersions,
    downloadEvalExport,
    createEvalCase,
    updateEvalCase,
    deleteEvalCase
} from '../../api/evalApi';

// Embedded theme (matching other admin components)
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539', quaternary: '#3f4147' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.7)', quaternary: 'rgba(255,255,255,0.5)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    gradients: { primaryAccent: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
    borderRadius: { sm: '8px', md: '12px' }
};

// Score color helper
const getScoreColor = (score) => {
    const safeScore = score ?? 0;
    if (safeScore >= 8) return theme.colors.status.success;
    if (safeScore >= 6) return theme.colors.status.warning;
    return theme.colors.status.error;
};

// Safe date formatting helper
const formatDate = (dateValue, includeTime = false) => {
    if (!dateValue) return 'N/A';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return 'N/A';
    return includeTime ? date.toLocaleString() : date.toLocaleDateString();
};

const AdminEvalDashboard = forwardRef((props, ref) => {
    // State management
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;

    // Data states
    const [dashboard, setDashboard] = useState({
        recentRuns: [],
        latestVersion: '',
        latestAvgScore: 0,
        totalCases: 0,
        activeCases: 0,
        casesByCategory: {},
        avgScoreByCategory: {},
        scoreTrend: []
    });
    const [cases, setCases] = useState([]);
    const [runs, setRuns] = useState([]);
    const [selectedRun, setSelectedRun] = useState(null);
    const [runResults, setRunResults] = useState([]);

    // Modal states
    const [startRunModalOpen, setStartRunModalOpen] = useState(false);
    const [compareModalOpen, setCompareModalOpen] = useState(false);
    const [viewResultsModalOpen, setViewResultsModalOpen] = useState(false);
    const [caseModalOpen, setCaseModalOpen] = useState(false);
    const [isCreatingCase, setIsCreatingCase] = useState(false);
    const [selectedCase, setSelectedCase] = useState(null);

    // Run form state
    const [promptVersion, setPromptVersion] = useState('');
    const [runNotes, setRunNotes] = useState('');
    const [startingRun, setStartingRun] = useState(false);

    // Compare state
    const [version1, setVersion1] = useState('');
    const [version2, setVersion2] = useState('');
    const [comparisonData, setComparisonData] = useState(null);
    const [comparing, setComparing] = useState(false);

    // Case form state
    const [caseForm, setCaseForm] = useState({
        category: '',
        question: '',
        expectedBehavior: '',
        difficulty: 3,
        context: '',
        isActive: true
    });

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuItem, setMenuItem] = useState(null);

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
        refresh: fetchAll
    }), []);

    // Fetch all data on mount
    useEffect(() => {
        fetchAll();
    }, []);

    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchDashboard(), fetchCases(), fetchRuns()]);
        setLoading(false);
    };

    const fetchDashboard = async () => {
        try {
            const result = await getEvalDashboard();
            if (result.success) {
                setDashboard(result.data || {});
            } else {
                toast.error(result.error || 'Failed to fetch dashboard');
            }
        } catch (error) {
            console.error('Error fetching dashboard:', error);
        }
    };

    const fetchCases = async () => {
        try {
            const result = await getEvalCases();
            if (result.success) {
                setCases(result.data || []);
            }
        } catch (error) {
            console.error('Error fetching cases:', error);
        }
    };

    const fetchRuns = async () => {
        try {
            const result = await getEvalRuns();
            if (result.success) {
                setRuns(result.data || []);
            }
        } catch (error) {
            console.error('Error fetching runs:', error);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchAll();
        setRefreshing(false);
        toast.success('Data refreshed');
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setPage(1);
    };

    // Start eval run
    const handleStartRun = async () => {
        if (!promptVersion.trim()) {
            toast.error('Please enter a prompt version');
            return;
        }
        setStartingRun(true);
        try {
            const result = await startEvalRun({ promptVersion: promptVersion.trim(), notes: runNotes.trim() });
            if (result.success) {
                toast.success('Eval run started successfully');
                setStartRunModalOpen(false);
                setPromptVersion('');
                setRunNotes('');
                await fetchAll();
            } else {
                toast.error(result.error || 'Failed to start run');
            }
        } catch (error) {
            toast.error('Failed to start run');
        } finally {
            setStartingRun(false);
        }
    };

    // View run results
    const handleViewResults = async (run) => {
        setSelectedRun(run);
        try {
            const result = await getEvalRunById(run.id);
            if (result.success) {
                setRunResults(result.data?.results || []);
                setViewResultsModalOpen(true);
            } else {
                toast.error(result.error || 'Failed to fetch results');
            }
        } catch (error) {
            toast.error('Failed to fetch results');
        }
    };

    // Export run
    const handleExport = async (version) => {
        const result = await downloadEvalExport(version, `eval-run-${version}.json`);
        if (result.success) {
            toast.success('Export downloaded');
        } else {
            toast.error(result.error || 'Failed to export');
        }
    };

    // Compare versions
    const handleCompare = async () => {
        if (!version1 || !version2) {
            toast.error('Please select two versions to compare');
            return;
        }
        setComparing(true);
        try {
            const result = await compareVersions(version1, version2);
            if (result.success) {
                setComparisonData(result.data);
            } else {
                toast.error(result.error || 'Failed to compare');
            }
        } catch (error) {
            toast.error('Failed to compare versions');
        } finally {
            setComparing(false);
        }
    };

    // Case CRUD
    const handleOpenCaseModal = (caseItem = null) => {
        if (caseItem) {
            setIsCreatingCase(false);
            setSelectedCase(caseItem);
            setCaseForm({
                category: caseItem.category || '',
                question: caseItem.question || '',
                expectedBehavior: caseItem.expectedBehavior || '',
                difficulty: caseItem.difficulty || 3,
                context: caseItem.context || '',
                isActive: caseItem.isActive !== false
            });
        } else {
            setIsCreatingCase(true);
            setSelectedCase(null);
            setCaseForm({
                category: '',
                question: '',
                expectedBehavior: '',
                difficulty: 3,
                context: '',
                isActive: true
            });
        }
        setCaseModalOpen(true);
    };

    const handleSaveCase = async () => {
        if (!caseForm.category || !caseForm.question || !caseForm.expectedBehavior) {
            toast.error('Please fill in all required fields');
            return;
        }
        try {
            const result = isCreatingCase
                ? await createEvalCase(caseForm)
                : await updateEvalCase(selectedCase.id, caseForm);
            if (result.success) {
                toast.success(isCreatingCase ? 'Case created' : 'Case updated');
                setCaseModalOpen(false);
                await fetchCases();
            } else {
                toast.error(result.error || 'Failed to save case');
            }
        } catch (error) {
            toast.error('Failed to save case');
        }
    };

    const handleDeleteCase = async (caseId) => {
        if (!window.confirm('Are you sure you want to delete this case?')) return;
        try {
            const result = await deleteEvalCase(caseId);
            if (result.success) {
                toast.success('Case deleted');
                await fetchCases();
            } else {
                toast.error(result.error || 'Failed to delete case');
            }
        } catch (error) {
            toast.error('Failed to delete case');
        }
    };

    // Menu handlers
    const handleMenuOpen = (event, item) => {
        setAnchorEl(event.currentTarget);
        setMenuItem(item);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuItem(null);
    };

    // Filter helpers
    const categories = [...new Set(cases.map(c => c.category).filter(Boolean))];
    const versions = [...new Set(runs.map(r => r.promptVersion).filter(Boolean))];

    const filteredCases = cases.filter(c => {
        const matchesSearch = !searchQuery ||
            c.question?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.category?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || c.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const paginatedCases = filteredCases.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    const totalPages = Math.ceil(filteredCases.length / rowsPerPage);

    // Render dashboard tab
    const renderDashboard = () => (
        <Box>
            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ p: 1.5, borderRadius: theme.borderRadius.sm, background: theme.colors.accent.primary + '20' }}>
                                    <ScoreIcon sx={{ color: theme.colors.accent.tertiary, fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>
                                        Latest Score
                                    </Typography>
                                    <Typography sx={{ color: getScoreColor(dashboard.latestAvgScore), fontWeight: 700, fontSize: '1.75rem' }}>
                                        {dashboard.latestAvgScore?.toFixed(1) || '0.0'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ p: 1.5, borderRadius: theme.borderRadius.sm, background: theme.colors.status.info + '20' }}>
                                    <TimelineIcon sx={{ color: theme.colors.status.info, fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>
                                        Latest Version
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 700, fontSize: '1.25rem' }}>
                                        {dashboard.latestVersion || 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ p: 1.5, borderRadius: theme.borderRadius.sm, background: theme.colors.status.success + '20' }}>
                                    <QuizIcon sx={{ color: theme.colors.status.success, fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>
                                        Total Cases
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 700, fontSize: '1.75rem' }}>
                                        {dashboard.totalCases || 0}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ p: 1.5, borderRadius: theme.borderRadius.sm, background: theme.colors.status.warning + '20' }}>
                                    <CheckCircleIcon sx={{ color: theme.colors.status.warning, fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>
                                        Active Cases
                                    </Typography>
                                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 700, fontSize: '1.75rem' }}>
                                        {dashboard.activeCases || 0}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Score by Category */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600, mb: 2 }}>
                            Cases by Category
                        </Typography>
                        {Object.entries(dashboard.casesByCategory || {}).map(([category, count]) => (
                            <Box key={category} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                <Typography sx={{ color: theme.colors.text.secondary }}>{category}</Typography>
                                <Chip label={count} size="small" sx={{ background: theme.colors.accent.primary, color: 'white' }} />
                            </Box>
                        ))}
                        {Object.keys(dashboard.casesByCategory || {}).length === 0 && (
                            <Typography sx={{ color: theme.colors.text.quaternary, textAlign: 'center', py: 2 }}>
                                No categories available
                            </Typography>
                        )}
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                        <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600, mb: 2 }}>
                            Average Score by Category
                        </Typography>
                        {Object.entries(dashboard.avgScoreByCategory || {}).map(([category, score]) => {
                            const safeScore = score ?? 0;
                            return (
                                <Box key={category} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    <Typography sx={{ color: theme.colors.text.secondary }}>{category}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={safeScore * 10}
                                            sx={{ width: 100, height: 8, borderRadius: 4, background: theme.colors.bg.tertiary, '& .MuiLinearProgress-bar': { background: getScoreColor(safeScore) } }}
                                        />
                                        <Typography sx={{ color: getScoreColor(safeScore), fontWeight: 600, minWidth: 35 }}>
                                            {safeScore.toFixed(1)}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                        {Object.keys(dashboard.avgScoreByCategory || {}).length === 0 && (
                            <Typography sx={{ color: theme.colors.text.quaternary, textAlign: 'center', py: 2 }}>
                                No score data available
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Score Trend Chart */}
            {(dashboard.scoreTrend || []).filter(d => d && d.avgScore != null).length > 0 && (
                <Paper sx={{ p: 3, mb: 4, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600, mb: 2 }}>
                        Score Trend
                    </Typography>
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={(dashboard.scoreTrend || []).filter(d => d && d.avgScore != null).map(d => ({ ...d, avgScore: Number(d.avgScore) }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border.primary} />
                            <XAxis
                                dataKey="version"
                                stroke={theme.colors.text.quaternary}
                                tick={{ fill: theme.colors.text.quaternary, fontSize: 12 }}
                            />
                            <YAxis
                                domain={[0, 10]}
                                stroke={theme.colors.text.quaternary}
                                tick={{ fill: theme.colors.text.quaternary, fontSize: 12 }}
                            />
                            <RechartsTooltip
                                contentStyle={{
                                    background: theme.colors.bg.tertiary,
                                    border: `1px solid ${theme.colors.border.primary}`,
                                    borderRadius: 8,
                                    color: theme.colors.text.primary
                                }}
                                formatter={(value) => {
                                    if (value === null || value === undefined) return ['N/A', 'Avg Score'];
                                    return [Number(value).toFixed(1), 'Avg Score'];
                                }}
                                labelFormatter={(label) => `Version: ${label}`}
                            />
                            <Line
                                type="monotone"
                                dataKey="avgScore"
                                stroke={theme.colors.accent.primary}
                                strokeWidth={2}
                                dot={{ fill: theme.colors.accent.primary, strokeWidth: 2, r: 4 }}
                                activeDot={{ r: 6, fill: theme.colors.accent.tertiary }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Paper>
            )}

            {/* Recent Runs */}
            <Paper sx={{ p: 3, background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                        Recent Runs
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => setStartRunModalOpen(true)}
                        sx={{ background: theme.gradients.primaryAccent, textTransform: 'none' }}
                    >
                        Start New Run
                    </Button>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: theme.colors.text.quaternary, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Version</TableCell>
                                <TableCell sx={{ color: theme.colors.text.quaternary, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Date</TableCell>
                                <TableCell sx={{ color: theme.colors.text.quaternary, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Avg Score</TableCell>
                                <TableCell sx={{ color: theme.colors.text.quaternary, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Status</TableCell>
                                <TableCell sx={{ color: theme.colors.text.quaternary, borderBottom: `1px solid ${theme.colors.border.primary}` }} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(dashboard.recentRuns || []).slice(0, 5).map((run) => (
                                <TableRow key={run.id} hover sx={{ '&:hover': { background: theme.colors.bg.tertiary } }}>
                                    <TableCell sx={{ color: theme.colors.text.secondary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                        {run.promptVersion}
                                    </TableCell>
                                    <TableCell sx={{ color: theme.colors.text.tertiary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                        {formatDate(run.startedAt || run.runDate || run.createdAt)}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                        <Typography sx={{ color: getScoreColor(run.avgScore), fontWeight: 600 }}>
                                            {run.avgScore?.toFixed(1) || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                        <Chip
                                            label={run.status || 'Completed'}
                                            size="small"
                                            sx={{
                                                background: run.status === 'running' ? theme.colors.status.warning + '20' : theme.colors.status.success + '20',
                                                color: run.status === 'running' ? theme.colors.status.warning : theme.colors.status.success
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right" sx={{ borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                        <Tooltip title="View Results">
                                            <IconButton size="small" onClick={() => handleViewResults(run)} sx={{ color: theme.colors.text.tertiary }}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Export">
                                            <IconButton size="small" onClick={() => handleExport(run.promptVersion)} sx={{ color: theme.colors.text.tertiary }}>
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {(!dashboard.recentRuns || dashboard.recentRuns.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ textAlign: 'center', color: theme.colors.text.quaternary, py: 4, borderBottom: 'none' }}>
                                        No eval runs yet. Start a new run to see results.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );

    // Render cases tab
    const renderCases = () => (
        <Box>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    placeholder="Search cases..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    sx={{
                        flex: 1,
                        minWidth: 200,
                        '& .MuiOutlinedInput-root': {
                            background: theme.colors.bg.secondary,
                            color: theme.colors.text.primary,
                            '& fieldset': { borderColor: theme.colors.border.primary }
                        }
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: theme.colors.text.quaternary }} /></InputAdornment>
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ color: theme.colors.text.quaternary }}>Category</InputLabel>
                    <Select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        label="Category"
                        sx={{ background: theme.colors.bg.secondary, color: theme.colors.text.primary, '& fieldset': { borderColor: theme.colors.border.primary } }}
                    >
                        <MenuItem value="all">All Categories</MenuItem>
                        {categories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
                    </Select>
                </FormControl>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenCaseModal()}
                    sx={{ background: theme.gradients.primaryAccent, textTransform: 'none' }}
                >
                    Add Case
                </Button>
            </Box>

            {/* Cases Table */}
            <TableContainer component={Paper} sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Category</TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Question</TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Difficulty</TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Status</TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedCases.map((caseItem) => (
                            <TableRow key={caseItem.id} hover sx={{ '&:hover': { background: theme.colors.bg.tertiary } }}>
                                <TableCell sx={{ color: theme.colors.text.secondary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    <Chip label={caseItem.category} size="small" sx={{ background: theme.colors.accent.primary + '20', color: theme.colors.accent.tertiary }} />
                                </TableCell>
                                <TableCell sx={{ color: theme.colors.text.secondary, borderBottom: `1px solid ${theme.colors.border.secondary}`, maxWidth: 400 }}>
                                    <Typography noWrap>{caseItem.question}</Typography>
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {[1, 2, 3, 4, 5].map((level) => (
                                            <Box
                                                key={level}
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    background: level <= caseItem.difficulty ? theme.colors.accent.primary : theme.colors.bg.tertiary
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    <Chip
                                        label={caseItem.isActive ? 'Active' : 'Inactive'}
                                        size="small"
                                        sx={{
                                            background: caseItem.isActive ? theme.colors.status.success + '20' : theme.colors.status.error + '20',
                                            color: caseItem.isActive ? theme.colors.status.success : theme.colors.status.error
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => handleOpenCaseModal(caseItem)} sx={{ color: theme.colors.text.tertiary }}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton size="small" onClick={() => handleDeleteCase(caseItem.id)} sx={{ color: theme.colors.status.error }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {paginatedCases.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ textAlign: 'center', color: theme.colors.text.quaternary, py: 4, borderBottom: 'none' }}>
                                    {searchQuery || categoryFilter !== 'all' ? 'No cases match your filters' : 'No eval cases yet'}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, newPage) => setPage(newPage)}
                        sx={{
                            '& .MuiPaginationItem-root': { color: theme.colors.text.secondary },
                            '& .Mui-selected': { background: theme.colors.accent.primary + ' !important', color: 'white' }
                        }}
                    />
                </Box>
            )}
        </Box>
    );

    // Render runs tab
    const renderRuns = () => (
        <Box>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => setStartRunModalOpen(true)}
                    sx={{ background: theme.gradients.primaryAccent, textTransform: 'none' }}
                >
                    Start New Run
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<CompareIcon />}
                    onClick={() => setCompareModalOpen(true)}
                    sx={{ borderColor: theme.colors.accent.primary, color: theme.colors.accent.tertiary, textTransform: 'none' }}
                >
                    Compare Versions
                </Button>
            </Box>

            {/* Runs Table */}
            <TableContainer component={Paper} sx={{ background: theme.colors.bg.secondary, border: `1px solid ${theme.colors.border.primary}` }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Run ID</TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Version</TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Date</TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Avg Score</TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Cases Run</TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }}>Status</TableCell>
                            <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600, borderBottom: `1px solid ${theme.colors.border.primary}` }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {runs.map((run) => (
                            <TableRow key={run.id} hover sx={{ '&:hover': { background: theme.colors.bg.tertiary } }}>
                                <TableCell sx={{ color: theme.colors.text.tertiary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    #{run.id}
                                </TableCell>
                                <TableCell sx={{ color: theme.colors.text.secondary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    <Chip label={run.promptVersion} size="small" sx={{ background: theme.colors.status.info + '20', color: theme.colors.status.info }} />
                                </TableCell>
                                <TableCell sx={{ color: theme.colors.text.tertiary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    {formatDate(run.startedAt || run.runDate || run.createdAt, true)}
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    <Typography sx={{ color: getScoreColor(run.avgScore), fontWeight: 600 }}>
                                        {run.avgScore?.toFixed(1) || 'N/A'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ color: theme.colors.text.tertiary, borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    {run.totalCases || run.casesRun || 0}
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    <Chip
                                        label={run.status || 'Completed'}
                                        size="small"
                                        sx={{
                                            background: run.status === 'running' ? theme.colors.status.warning + '20' :
                                                run.status === 'failed' ? theme.colors.status.error + '20' : theme.colors.status.success + '20',
                                            color: run.status === 'running' ? theme.colors.status.warning :
                                                run.status === 'failed' ? theme.colors.status.error : theme.colors.status.success
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ borderBottom: `1px solid ${theme.colors.border.secondary}` }}>
                                    <Tooltip title="View Results">
                                        <IconButton size="small" onClick={() => handleViewResults(run)} sx={{ color: theme.colors.text.tertiary }}>
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Export JSON">
                                        <IconButton size="small" onClick={() => handleExport(run.id)} sx={{ color: theme.colors.text.tertiary }}>
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {runs.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} sx={{ textAlign: 'center', color: theme.colors.text.quaternary, py: 4, borderBottom: 'none' }}>
                                    No eval runs yet. Start a new run to evaluate your prompts.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    return (
        <Box sx={{ p: 3, background: theme.colors.bg.primary, minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ color: theme.colors.text.primary, fontWeight: 700, mb: 0.5 }}>
                        Eval Pipeline
                    </Typography>
                    <Typography sx={{ color: theme.colors.text.quaternary }}>
                        Evaluate and track Q AI prompt performance
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    sx={{ borderColor: theme.colors.border.primary, color: theme.colors.text.secondary, textTransform: 'none' }}
                >
                    Refresh
                </Button>
            </Box>

            {/* Loading */}
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: theme.colors.border.primary, mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{
                        '& .MuiTab-root': { color: theme.colors.text.tertiary, textTransform: 'none', fontWeight: 500 },
                        '& .Mui-selected': { color: theme.colors.accent.tertiary },
                        '& .MuiTabs-indicator': { background: theme.colors.accent.primary }
                    }}
                >
                    <Tab icon={<AssessmentIcon />} iconPosition="start" label="Dashboard" />
                    <Tab icon={<QuizIcon />} iconPosition="start" label="Cases" />
                    <Tab icon={<TimelineIcon />} iconPosition="start" label="Runs" />
                </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && renderDashboard()}
            {activeTab === 1 && renderCases()}
            {activeTab === 2 && renderRuns()}

            {/* Start Run Modal */}
            <Dialog open={startRunModalOpen} onClose={() => setStartRunModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { background: theme.colors.bg.secondary, color: theme.colors.text.primary } }}>
                <DialogTitle sx={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    Start New Eval Run
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <TextField
                        fullWidth
                        label="Prompt Version"
                        placeholder="e.g., v1.0, v2.0-beta"
                        value={promptVersion}
                        onChange={(e) => setPromptVersion(e.target.value)}
                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { background: theme.colors.bg.tertiary, color: theme.colors.text.primary }, '& .MuiInputLabel-root': { color: theme.colors.text.quaternary } }}
                    />
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Notes (optional)"
                        placeholder="Add any notes about this run..."
                        value={runNotes}
                        onChange={(e) => setRunNotes(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { background: theme.colors.bg.tertiary, color: theme.colors.text.primary }, '& .MuiInputLabel-root': { color: theme.colors.text.quaternary } }}
                    />
                    <Alert severity="info" sx={{ mt: 2, background: theme.colors.status.info + '10', color: theme.colors.text.secondary }}>
                        This will run all {dashboard.activeCases || 0} active eval cases against the current prompts and score the responses.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, p: 2 }}>
                    <Button onClick={() => setStartRunModalOpen(false)} sx={{ color: theme.colors.text.tertiary }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleStartRun}
                        disabled={startingRun || !promptVersion.trim()}
                        startIcon={startingRun ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                        sx={{ background: theme.gradients.primaryAccent }}
                    >
                        {startingRun ? 'Starting...' : 'Start Run'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Results Modal */}
            <Dialog open={viewResultsModalOpen} onClose={() => setViewResultsModalOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { background: theme.colors.bg.secondary, color: theme.colors.text.primary, maxHeight: '90vh' } }}>
                <DialogTitle sx={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    Run Results - {selectedRun?.promptVersion}
                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.875rem' }}>
                        {selectedRun && formatDate(selectedRun.startedAt || selectedRun.runDate || selectedRun.createdAt, true)}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600 }}>Category</TableCell>
                                    <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600 }}>Question</TableCell>
                                    <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600 }}>Response</TableCell>
                                    <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600 }}>Score</TableCell>
                                    <TableCell sx={{ color: theme.colors.text.quaternary, fontWeight: 600 }}>Reasoning</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {runResults.map((result, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell sx={{ color: theme.colors.text.tertiary, verticalAlign: 'top' }}>
                                            <Chip label={result.category} size="small" sx={{ background: theme.colors.accent.primary + '20', color: theme.colors.accent.tertiary }} />
                                        </TableCell>
                                        <TableCell sx={{ color: theme.colors.text.secondary, maxWidth: 200, verticalAlign: 'top' }}>
                                            <Typography sx={{ fontSize: '0.875rem' }}>{result.question}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: theme.colors.text.tertiary, maxWidth: 300, verticalAlign: 'top' }}>
                                            <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{result.response?.substring(0, 200)}{result.response?.length > 200 ? '...' : ''}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Typography sx={{ color: getScoreColor(result.score), fontWeight: 700, fontSize: '1.25rem' }}>
                                                {result.score}/10
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: theme.colors.text.tertiary, maxWidth: 250, verticalAlign: 'top' }}>
                                            <Typography sx={{ fontSize: '0.875rem' }}>{result.reasoning}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {runResults.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ textAlign: 'center', color: theme.colors.text.quaternary, py: 4 }}>
                                            No results available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, p: 2 }}>
                    <Button onClick={() => selectedRun && handleExport(selectedRun.promptVersion)} startIcon={<DownloadIcon />} sx={{ color: theme.colors.accent.tertiary }}>
                        Export JSON
                    </Button>
                    <Button onClick={() => setViewResultsModalOpen(false)} variant="contained" sx={{ background: theme.gradients.primaryAccent }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Compare Modal */}
            <Dialog open={compareModalOpen} onClose={() => setCompareModalOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { background: theme.colors.bg.secondary, color: theme.colors.text.primary, maxHeight: '90vh' } }}>
                <DialogTitle sx={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    Compare Versions
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={5}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: theme.colors.text.quaternary }}>Version 1</InputLabel>
                                <Select
                                    value={version1}
                                    onChange={(e) => setVersion1(e.target.value)}
                                    label="Version 1"
                                    sx={{ background: theme.colors.bg.tertiary, color: theme.colors.text.primary }}
                                >
                                    {versions.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CompareIcon sx={{ color: theme.colors.text.quaternary }} />
                        </Grid>
                        <Grid item xs={5}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: theme.colors.text.quaternary }}>Version 2</InputLabel>
                                <Select
                                    value={version2}
                                    onChange={(e) => setVersion2(e.target.value)}
                                    label="Version 2"
                                    sx={{ background: theme.colors.bg.tertiary, color: theme.colors.text.primary }}
                                >
                                    {versions.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Button
                        variant="contained"
                        onClick={handleCompare}
                        disabled={comparing || !version1 || !version2}
                        startIcon={comparing ? <CircularProgress size={16} color="inherit" /> : <CompareIcon />}
                        sx={{ background: theme.gradients.primaryAccent, mb: 3 }}
                    >
                        {comparing ? 'Comparing...' : 'Compare'}
                    </Button>

                    {comparisonData && (
                        <Box>
                            <Grid container spacing={3} sx={{ mb: 3 }}>
                                <Grid item xs={6}>
                                    <Paper sx={{ p: 2, background: theme.colors.bg.tertiary, textAlign: 'center' }}>
                                        <Typography sx={{ color: theme.colors.text.quaternary, mb: 1 }}>{version1}</Typography>
                                        <Typography sx={{ color: getScoreColor(comparisonData.version1Score ?? 0), fontWeight: 700, fontSize: '2rem' }}>
                                            {comparisonData.version1Score?.toFixed(1) ?? 'N/A'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6}>
                                    <Paper sx={{ p: 2, background: theme.colors.bg.tertiary, textAlign: 'center' }}>
                                        <Typography sx={{ color: theme.colors.text.quaternary, mb: 1 }}>{version2}</Typography>
                                        <Typography sx={{ color: getScoreColor(comparisonData.version2Score ?? 0), fontWeight: 700, fontSize: '2rem' }}>
                                            {comparisonData.version2Score?.toFixed(1) ?? 'N/A'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                            {comparisonData.difference != null && (
                                <Alert
                                    severity={comparisonData.difference > 0 ? 'success' : comparisonData.difference < 0 ? 'error' : 'info'}
                                    icon={comparisonData.difference > 0 ? <TrendingUpIcon /> : comparisonData.difference < 0 ? <TrendingDownIcon /> : null}
                                    sx={{ background: theme.colors.bg.tertiary }}
                                >
                                    {version2} {comparisonData.difference > 0 ? 'improved' : comparisonData.difference < 0 ? 'regressed' : 'is the same'} by {Math.abs(comparisonData.difference ?? 0).toFixed(1)} points compared to {version1}
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, p: 2 }}>
                    <Button onClick={() => { setCompareModalOpen(false); setComparisonData(null); }} variant="contained" sx={{ background: theme.gradients.primaryAccent }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Case Edit Modal */}
            <Dialog open={caseModalOpen} onClose={() => setCaseModalOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { background: theme.colors.bg.secondary, color: theme.colors.text.primary } }}>
                <DialogTitle sx={{ borderBottom: `1px solid ${theme.colors.border.primary}` }}>
                    {isCreatingCase ? 'Add Eval Case' : 'Edit Eval Case'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Category"
                                placeholder="e.g., ea_basics, code_debug"
                                value={caseForm.category}
                                onChange={(e) => setCaseForm({ ...caseForm, category: e.target.value })}
                                sx={{ '& .MuiOutlinedInput-root': { background: theme.colors.bg.tertiary, color: theme.colors.text.primary }, '& .MuiInputLabel-root': { color: theme.colors.text.quaternary } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme.colors.text.quaternary }}>Difficulty</InputLabel>
                                <Select
                                    value={caseForm.difficulty}
                                    onChange={(e) => setCaseForm({ ...caseForm, difficulty: e.target.value })}
                                    label="Difficulty"
                                    sx={{ background: theme.colors.bg.tertiary, color: theme.colors.text.primary }}
                                >
                                    {[1, 2, 3, 4, 5].map(level => <MenuItem key={level} value={level}>Level {level}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Question"
                                placeholder="The test question to ask the AI..."
                                value={caseForm.question}
                                onChange={(e) => setCaseForm({ ...caseForm, question: e.target.value })}
                                sx={{ '& .MuiOutlinedInput-root': { background: theme.colors.bg.tertiary, color: theme.colors.text.primary }, '& .MuiInputLabel-root': { color: theme.colors.text.quaternary } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Expected Behavior"
                                placeholder="What the AI should do/say..."
                                value={caseForm.expectedBehavior}
                                onChange={(e) => setCaseForm({ ...caseForm, expectedBehavior: e.target.value })}
                                sx={{ '& .MuiOutlinedInput-root': { background: theme.colors.bg.tertiary, color: theme.colors.text.primary }, '& .MuiInputLabel-root': { color: theme.colors.text.quaternary } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Context (optional)"
                                placeholder="Additional context for the test..."
                                value={caseForm.context}
                                onChange={(e) => setCaseForm({ ...caseForm, context: e.target.value })}
                                sx={{ '& .MuiOutlinedInput-root': { background: theme.colors.bg.tertiary, color: theme.colors.text.primary }, '& .MuiInputLabel-root': { color: theme.colors.text.quaternary } }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ borderTop: `1px solid ${theme.colors.border.primary}`, p: 2 }}>
                    <Button onClick={() => setCaseModalOpen(false)} sx={{ color: theme.colors.text.tertiary }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveCase}
                        sx={{ background: theme.gradients.primaryAccent }}
                    >
                        {isCreatingCase ? 'Create' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
});

export default AdminEvalDashboard;
